import clsx from 'clsx';
import { forwardRef, Fragment, useState } from 'react';

import Cross16 from '../components/svg/16x16/Cross16.svg';
import Refresh16 from '../components/svg/16x16/Refresh16.svg';
import Chevron from '../components/svg/24x24/ChevronRight.svg';
import styles from './EditableLotteryEntry.module.css';
import LotteryEntry from './LotteryEntry';

interface Props {
  variant?: undefined | 'V2';
  main: (number | null)[];
  bonus: (number | null)[];
  lineNumber: number;
  onRandomiseClick?: () => void;
  offerText?: string | undefined;
  showTapToChange?: boolean;
  onRemoveClick?: () => void;
  onEditClick?: (data?: 'BONUS_BALL' | undefined) => void;
  background?: 'onDarkBlue';
  pulse?: boolean;
  /* Collapse settings/functions */
  toggleCollapse?: () => void;
  collapsed?: boolean;
  firstItemCollapsed?: boolean;
  disabled?: boolean;
  stackLength?: number;
  stackIndex?: number;
}

const EditableLotteryEntry = forwardRef<HTMLDivElement, Props>(
  function EditableLotteryEntry(
    {
      variant,
      main,
      bonus,
      lineNumber,
      onRandomiseClick,
      offerText,
      showTapToChange,
      onRemoveClick,
      onEditClick,
      toggleCollapse,
      collapsed,
      firstItemCollapsed,
      disabled,
      stackLength,
      stackIndex,
      background,
      pulse,
    },
    ref,
  ) {
    const [flipChevron, setFlipChevron] = useState(false);
    const LotteryEntryContainer = onEditClick ? 'button' : Fragment;
    const lotteryEntryContainerProps = onEditClick
      ? {
          onClick: (e: React.MouseEvent) => {
            const isBonusBall = (e.target as HTMLElement).closest(
              '[data-lottery-entry-bonus-ball]',
            );
            onEditClick?.(isBonusBall ? 'BONUS_BALL' : undefined);
          },
          'aria-label': 'Edit entries',
          className: clsx('buttonReset', styles.entriesContainer),
        }
      : undefined;

    const handleToggleCollapse = () => {
      toggleCollapse && toggleCollapse();
      setFlipChevron(!flipChevron);
    };

    return (
      <div
        ref={ref}
        className={clsx(
          'daymadeTypographySMedium',
          styles.container,
          variant === 'V2' && styles.variant2,
          collapsed && styles.collapsed,
          background === 'onDarkBlue' && styles.onDarkBlue,
          !collapsed && pulse && styles.pulse,
        )}
        style={{ ['--stack-index' as string]: stackIndex ?? 0 }}
        aria-hidden={collapsed || undefined}
      >
        <div
          className={clsx(
            styles.titleContainer,
            firstItemCollapsed && styles.titleContainerToFont,
          )}
        >
          <div className={clsx(styles.title, collapsed && styles.collapsed)}>
            {firstItemCollapsed && stackLength && stackLength > 1
              ? 'Entries'
              : 'Entry'}{' '}
            {lineNumber}
            {firstItemCollapsed && stackLength === 2
              ? ' & 2'
              : firstItemCollapsed && stackLength === 3
                ? ', 2 & 3'
                : firstItemCollapsed && stackLength === 4
                  ? ', 2, 3 & 4'
                  : ''}
          </div>
          {offerText && (
            <div className={clsx(styles.offer, collapsed && styles.collapsed)}>
              Entry {offerText}
            </div>
          )}
          {showTapToChange && onEditClick && (
            <button
              className={clsx('buttonReset', styles.changeHintText)}
              onClick={() => onEditClick?.()}
            >
              Tap to change
            </button>
          )}
          {onRemoveClick && (
            <div
              className={clsx(
                styles.removeContainer,
                collapsed && styles.collapsed,
              )}
            >
              <button
                className={clsx('buttonReset', styles.removeButton)}
                aria-label="Remove entry line"
                onClick={onRemoveClick}
              >
                <Cross16 />
              </button>
            </div>
          )}
        </div>
        <div className={styles.lineContainer}>
          <button
            className={clsx(
              styles.randomise,
              'buttonReset',
              (collapsed || firstItemCollapsed) && styles.collapsed,
            )}
            onClick={onRandomiseClick}
            aria-label="Randomise numbers for this entry"
          >
            <Refresh16 />
          </button>
          <LotteryEntryContainer {...lotteryEntryContainerProps}>
            <LotteryEntry
              main={main}
              bonus={bonus}
              disabled={disabled}
              firstEntryDisabled={firstItemCollapsed}
            />
          </LotteryEntryContainer>
          <button
            className={clsx(
              styles.toggleCollapse,
              firstItemCollapsed && styles.showToggle,
              flipChevron && styles.flipChevron,
              'buttonReset',
            )}
            onClick={handleToggleCollapse}
            aria-label="Hide and show all entries"
          >
            <Chevron />
          </button>
        </div>
      </div>
    );
  },
);

export default EditableLotteryEntry;
